import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaHistory,
  FaRoad,
  FaGasPump,
  FaCar,
  FaUserCircle,
  FaSignOutAlt,
  FaSave,
  FaCalculator
} from 'react-icons/fa';
import Header from '../components/Common/Header';
import { useAuth } from '../contexts/AuthContext';
import {
  estimateGeneralFuelConsumption,
  inferVehicleProfileFromDetails
} from '../services/vehicleConsumptionEstimator';
import '../css/style.css';
import '../css/profile.css';
import Seo from '../components/Common/Seo';

const isDevelopment = process.env.NODE_ENV !== 'production';
const devWarn = (...args) => {
  if (isDevelopment) {
    console.warn(...args);
  }
};

const parseVehicleCatalogCsv = (csvText) => {
  const lines = csvText.split('\n').map((line) => line.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const hasVariantSchema = headers.includes('variant') && headers.includes('fuel_consumption_l_100km');
  const getValue = (cols, key) => {
    const idx = headers.indexOf(key);
    return idx >= 0 ? (cols[idx] || '').trim() : '';
  };

  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cols = lines[i].split(',').map((c) => c.trim());
    if (cols.length < 5) continue;
    if (hasVariantSchema) {
      const make = getValue(cols, 'make');
      const modelName = getValue(cols, 'model');
      const year = getValue(cols, 'year');
      const variant = getValue(cols, 'variant');
      const bodyClass = getValue(cols, 'body_class');
      const consumption = getValue(cols, 'fuel_consumption_l_100km');
      const transmission = getValue(cols, 'transmission');
      const fuelTypeRaw = getValue(cols, 'fuel_type');
      const normalizedBody = (bodyClass || '').toLowerCase();
      const normalizedFuel = (fuelTypeRaw || '').trim();
      const isLargeClass = ['pickup', 'mpv', 'panel van', 'minibus', 'truck', 'van'].some((token) =>
        normalizedBody.includes(token)
      );
      rows.push({
        make,
        model: modelName,
        variant: variant || '',
        year: year || '',
        fuel_type: normalizedFuel === 'Hybrid' ? 'Petrol' : normalizedFuel,
        vehicle_class: isLargeClass ? 'Class 2' : 'Class 1',
        avg_consumption: Number(consumption),
        body_class: bodyClass,
        transmission
      });
    } else {
      let make;
      let model;
      let year;
      let fuel_type;
      let vehicle_class;
      let avg_consumption;
      if (cols.length >= 6) {
        [make, model, year, fuel_type, vehicle_class, avg_consumption] = cols;
      } else {
        [make, model, fuel_type, vehicle_class, avg_consumption] = cols;
        year = '';
      }
      rows.push({
        make,
        model,
        variant: '',
        year,
        fuel_type,
        vehicle_class,
        avg_consumption: Number(avg_consumption),
        transmission: ''
      });
    }
  }
  return rows;
};

const defaultSettings = {
  vehicle_make: '',
  vehicle_model: '',
  vehicle_variant: '',
  vehicle_year: '',
  transmission: '',
  fuel_type: 'Petrol',
  vehicle_class: 'Class 1',
  avg_consumption: '8.0',
  avoid_highways: false,
  avoid_tolls: false
};

const VEHICLE_CATALOG = {
  Toyota: ['Corolla 1.8', 'Corolla Cross 1.8', 'Hilux 2.8 GD-6', 'Fortuner 2.8 GD-6', 'Yaris 1.5'],
  Volkswagen: ['Polo 1.0 TSI', 'Golf GTI 2.0', 'Tiguan 2.0 TDI', 'Amarok 3.0 TDI'],
  Ford: ['Fiesta 1.0 EcoBoost', 'Focus 1.5', 'Ranger 2.0 Bi-Turbo', 'Everest 2.0'],
  BMW: ['320i', '330d', 'X3 xDrive20d', 'X5 xDrive30d'],
  Mercedes: ['C200', 'C220d', 'GLC 220d', 'Vito 2.0'],
  Hyundai: ['i20 1.4', 'Creta 1.5', 'Tucson 2.0', 'H1 2.5'],
  Kia: ['Picanto 1.0', 'Rio 1.4', 'Sportage 2.0', 'Sorento 2.2 CRDi'],
  Nissan: ['Micra 1.2', 'Qashqai 1.3', 'X-Trail 2.5', 'Navara 2.5 dCi'],
  Isuzu: ['D-Max 1.9 Ddi', 'D-Max 3.0 Ddi', 'MU-X 3.0'],
  Mazda: ['Mazda2 1.5', 'Mazda3 2.0', 'CX-5 2.2D', 'BT-50 3.0']
};

const VEHICLE_MODEL_PRESETS = {
  'hilux 2.8 gd-6': { fuelType: 'Diesel', vehicleClass: 'Class 2', avgConsumption: 9.2 },
  'ranger 2.0 bi-turbo': { fuelType: 'Diesel', vehicleClass: 'Class 2', avgConsumption: 8.7 },
  'amarok 3.0 tdi': { fuelType: 'Diesel', vehicleClass: 'Class 2', avgConsumption: 9.4 },
  'd-max 3.0 ddi': { fuelType: 'Diesel', vehicleClass: 'Class 2', avgConsumption: 8.9 },
  'navara 2.5 dci': { fuelType: 'Diesel', vehicleClass: 'Class 2', avgConsumption: 9.1 },
  'fortuner 2.8 gd-6': { fuelType: 'Diesel', vehicleClass: 'Class 2', avgConsumption: 8.5 },
  'vito 2.0': { fuelType: 'Diesel', vehicleClass: 'Class 2', avgConsumption: 8.4 },
  'h1 2.5': { fuelType: 'Diesel', vehicleClass: 'Class 2', avgConsumption: 9.6 },
  'sorento 2.2 crdi': { fuelType: 'Diesel', vehicleClass: 'Class 2', avgConsumption: 8.1 },
  'tiguan 2.0 tdi': { fuelType: 'Diesel', vehicleClass: 'Class 1', avgConsumption: 7.3 },
  'polo 1.0 tsi': { fuelType: 'Petrol', vehicleClass: 'Class 1', avgConsumption: 5.7 },
  'corolla 1.8': { fuelType: 'Petrol', vehicleClass: 'Class 1', avgConsumption: 6.6 }
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, photoURL } = useAuth();
  const [settings, setSettings] = useState(defaultSettings);
  const [vehicleCatalogRows, setVehicleCatalogRows] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [saveMessage, setSaveMessage] = useState('');
  const [estimateMessage, setEstimateMessage] = useState('');
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [showVehicleSuggestions, setShowVehicleSuggestions] = useState(false);
  const vehicleSuggestions = useMemo(() => {
    if (vehicleCatalogRows.length) {
      return vehicleCatalogRows
        .map((row) => ({
          key: `${row.make}|${row.model}|${row.variant || ''}|${row.year || ''}`,
          make: row.make,
          model: row.model,
          variant: row.variant || '',
          year: row.year || '',
          fuel_type: row.fuel_type,
          vehicle_class: row.vehicle_class,
          transmission: row.transmission || '',
          avg_consumption: row.avg_consumption,
          label: [row.make, row.model, row.variant].filter(Boolean).join(' ')
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    return Object.entries(VEHICLE_CATALOG)
      .flatMap(([make, models]) => models.map((model) => ({
        key: `${make}|${model}`,
        make,
        model,
        variant: '',
        year: '',
        fuel_type: '',
        vehicle_class: '',
        transmission: '',
        avg_consumption: null,
        label: `${make} ${model}`
      })))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [vehicleCatalogRows]);

  const getScopedStorageKey = useMemo(() => {
    const uid = user?.uid;
    return (key) => (uid ? `whe2_${uid}_${key}` : `whe2_guest_${key}`);
  }, [user?.uid]);

  useEffect(() => {
    setProfileImageFailed(false);
  }, [user?.uid, photoURL]);

  const userPhotoURL = photoURL || null;

  useEffect(() => {
    let mounted = true;

    const loadCatalog = async () => {
      try {
        const response = await fetch('/data/vehicle_catalog.csv');
        if (!response.ok) return;
        const csv = await response.text();
        const rows = parseVehicleCatalogCsv(csv);
        if (mounted && rows.length) {
          setVehicleCatalogRows(rows);
        }
      } catch (err) {
        devWarn('Vehicle CSV could not be loaded. Falling back to local list.', err);
      }
    };

    loadCatalog();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!user) return;

    const fuelType = localStorage.getItem(getScopedStorageKey('fuel_type'));
    const vehicleClass = localStorage.getItem(getScopedStorageKey('vehicle_class'));
    const fuelConsumption = localStorage.getItem(getScopedStorageKey('fuel_consumption'));
    const vehicleMake = localStorage.getItem(getScopedStorageKey('vehicle_make'));
    const vehicleModel = localStorage.getItem(getScopedStorageKey('vehicle_model'));
    const vehicleVariant = localStorage.getItem(getScopedStorageKey('vehicle_variant'));
    const vehicleYear = localStorage.getItem(getScopedStorageKey('vehicle_year'));
    const transmission = localStorage.getItem(getScopedStorageKey('transmission'));
    const avoidHighways = localStorage.getItem(getScopedStorageKey('avoid_highways'));
    const avoidTolls = localStorage.getItem(getScopedStorageKey('avoid_tolls'));
    const searches = localStorage.getItem(getScopedStorageKey('recent_searches'));

    setSettings({
      vehicle_make: vehicleMake || defaultSettings.vehicle_make,
      vehicle_model: vehicleModel || defaultSettings.vehicle_model,
      vehicle_variant: vehicleVariant || defaultSettings.vehicle_variant,
      vehicle_year: vehicleYear || defaultSettings.vehicle_year,
      transmission: transmission || defaultSettings.transmission,
      fuel_type: fuelType || defaultSettings.fuel_type,
      vehicle_class: vehicleClass || defaultSettings.vehicle_class,
      avg_consumption: fuelConsumption || defaultSettings.avg_consumption,
      avoid_highways: avoidHighways === 'true',
      avoid_tolls: avoidTolls === 'true'
    });
    setVehicleSearch([vehicleMake, vehicleModel, vehicleVariant].filter(Boolean).join(' ').trim());

    setRecentSearches(searches ? JSON.parse(searches) : []);
  }, [user, getScopedStorageKey]);

  const handleSettingChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const filteredVehicleSuggestions = useMemo(() => {
    const q = vehicleSearch.trim().toLowerCase();
    const topList = vehicleSuggestions.slice(0, 10);
    if (!q) return topList;
    const tokens = q.split(/\s+/).filter(Boolean);
    return vehicleSuggestions
      .filter((entry) => {
        const hay = `${entry.make} ${entry.model} ${entry.variant}`.toLowerCase();
        return tokens.every((token) => hay.includes(token));
      })
      .slice(0, 10);
  }, [vehicleSearch, vehicleSuggestions]);

  const yearSuggestions = useMemo(() => {
    if (!vehicleCatalogRows.length) return [];
    const make = settings.vehicle_make.trim().toLowerCase();
    const model = settings.vehicle_model.trim().toLowerCase();
    const variant = settings.vehicle_variant.trim().toLowerCase();
    const rows = vehicleCatalogRows.filter((row) => {
      const makeMatch = !make || row.make.toLowerCase() === make;
      const modelMatch = !model || row.model.toLowerCase() === model;
      const variantMatch = !variant || (row.variant || '').toLowerCase() === variant;
      return makeMatch && modelMatch && variantMatch && row.year;
    });
    return Array.from(new Set(rows.map((row) => row.year))).sort((a, b) => Number(b) - Number(a));
  }, [settings.vehicle_make, settings.vehicle_model, settings.vehicle_variant, vehicleCatalogRows]);

  const fuelTypeOptions = useMemo(() => {
    if (!vehicleCatalogRows.length) return ['Petrol', 'Diesel'];
    const make = settings.vehicle_make.trim().toLowerCase();
    const model = settings.vehicle_model.trim().toLowerCase();
    const variant = settings.vehicle_variant.trim().toLowerCase();
    const year = settings.vehicle_year;

    if (!make && !model) return ['Petrol', 'Diesel'];

    const rows = vehicleCatalogRows.filter((row) => {
      const makeMatch = !make || row.make.toLowerCase() === make;
      const modelMatch = !model || row.model.toLowerCase() === model;
      const variantMatch = !variant || (row.variant || '').toLowerCase() === variant;
      const yearMatch = !year || row.year === year;
      return makeMatch && modelMatch && variantMatch && yearMatch;
    });

    const types = Array.from(new Set(rows.map((row) => row.fuel_type))).filter(Boolean);
    return types.length ? types : ['Petrol', 'Diesel'];
  }, [settings.vehicle_make, settings.vehicle_model, settings.vehicle_variant, settings.vehicle_year, vehicleCatalogRows]);

  const transmissionOptions = useMemo(() => {
    if (!vehicleCatalogRows.length) return ['Manual', 'Automatic'];
    const make = settings.vehicle_make.trim().toLowerCase();
    const model = settings.vehicle_model.trim().toLowerCase();
    const variant = settings.vehicle_variant.trim().toLowerCase();
    const year = settings.vehicle_year;
    const rows = vehicleCatalogRows.filter((row) => {
      const makeMatch = !make || row.make.toLowerCase() === make;
      const modelMatch = !model || row.model.toLowerCase() === model;
      const variantMatch = !variant || (row.variant || '').toLowerCase() === variant;
      const yearMatch = !year || row.year === year;
      return makeMatch && modelMatch && variantMatch && yearMatch;
    });
    const options = Array.from(new Set(rows.map((row) => row.transmission).filter(Boolean)));
    return options.length ? options : ['Manual', 'Automatic'];
  }, [settings.vehicle_make, settings.vehicle_model, settings.vehicle_variant, settings.vehicle_year, vehicleCatalogRows]);

  useEffect(() => {
    if (!fuelTypeOptions.length) return;
    if (!fuelTypeOptions.includes(settings.fuel_type)) {
      setSettings((prev) => ({ ...prev, fuel_type: fuelTypeOptions[0] }));
      return;
    }
    if (fuelTypeOptions.length === 1 && settings.fuel_type !== fuelTypeOptions[0]) {
      setSettings((prev) => ({ ...prev, fuel_type: fuelTypeOptions[0] }));
    }
  }, [fuelTypeOptions, settings.fuel_type]);

  useEffect(() => {
    if (!transmissionOptions.length) return;
    if (!transmissionOptions.includes(settings.transmission)) {
      setSettings((prev) => ({ ...prev, transmission: transmissionOptions[0] }));
      return;
    }
    if (transmissionOptions.length === 1 && settings.transmission !== transmissionOptions[0]) {
      setSettings((prev) => ({ ...prev, transmission: transmissionOptions[0] }));
    }
  }, [transmissionOptions, settings.transmission]);

  const getAutoProfile = (make, model, variant = settings.vehicle_variant, year = settings.vehicle_year) => {
    if (vehicleCatalogRows.length) {
      const exact = vehicleCatalogRows.find((row) =>
        row.make.toLowerCase() === make.trim().toLowerCase() &&
        row.model.toLowerCase() === model.trim().toLowerCase() &&
        (!variant || (row.variant || '').toLowerCase() === variant.trim().toLowerCase()) &&
        (!year || row.year === year)
      );
      const byModel = vehicleCatalogRows.find((row) =>
        row.model.toLowerCase() === model.trim().toLowerCase() &&
        (!variant || (row.variant || '').toLowerCase() === variant.trim().toLowerCase()) &&
        (!year || row.year === year)
      );
      const match = exact || byModel;

      if (match) {
        return {
          fuelType: match.fuel_type,
          vehicleClass: match.vehicle_class,
          avgConsumption: match.avg_consumption,
          transmission: match.transmission || ''
        };
      }
    }

    const preset = VEHICLE_MODEL_PRESETS[model.trim().toLowerCase()];
    if (preset) {
      return {
        fuelType: preset.fuelType,
        vehicleClass: preset.vehicleClass,
        avgConsumption: preset.avgConsumption,
        transmission: ''
      };
    }
    const inferred = inferVehicleProfileFromDetails({ make, model: `${model} ${variant}`.trim() });
    return {
      fuelType: inferred.fuelType,
      vehicleClass: inferred.vehicleClass,
      avgConsumption: inferred.avgConsumption,
      transmission: ''
    };
  };

  const applyAutoPopulate = (make, model, variant = settings.vehicle_variant) => {
    if (!make && !model) return;
    const auto = getAutoProfile(make, model, variant);
    setSettings((prev) => ({
      ...prev,
      vehicle_make: make,
      vehicle_model: model,
      vehicle_variant: variant,
      fuel_type: auto.fuelType || prev.fuel_type,
      vehicle_class: auto.vehicleClass || prev.vehicle_class,
      transmission: auto.transmission || prev.transmission,
      avg_consumption: String(auto.avgConsumption ?? prev.avg_consumption)
    }));
  };

  const handleVehicleSearchSelect = (selection) => {
    if (!selection) return;
    setVehicleSearch(selection.label);
    setSettings((prev) => ({
      ...prev,
      vehicle_make: selection.make || '',
      vehicle_model: selection.model || '',
      vehicle_variant: selection.variant || '',
      vehicle_year: selection.year || prev.vehicle_year,
      fuel_type: selection.fuel_type || prev.fuel_type,
      vehicle_class: selection.vehicle_class || prev.vehicle_class,
      transmission: selection.transmission || prev.transmission,
      avg_consumption: String(selection.avg_consumption ?? prev.avg_consumption)
    }));
    applyAutoPopulate(selection.make || '', selection.model || '', selection.variant || '');
  };

  const handleVehicleYearChange = (value) => {
    const auto = getAutoProfile(settings.vehicle_make, settings.vehicle_model, settings.vehicle_variant, value);
    setSettings((prev) => ({
      ...prev,
      vehicle_year: value,
      fuel_type: auto.fuelType || prev.fuel_type,
      vehicle_class: auto.vehicleClass || prev.vehicle_class,
      transmission: auto.transmission || prev.transmission,
      avg_consumption: String(auto.avgConsumption ?? prev.avg_consumption)
    }));
  };

  const persistSettings = () => {
    if (!user) return;

    localStorage.setItem(getScopedStorageKey('vehicle_make'), settings.vehicle_make);
    localStorage.setItem(getScopedStorageKey('vehicle_model'), settings.vehicle_model);
    localStorage.setItem(getScopedStorageKey('vehicle_variant'), settings.vehicle_variant);
    localStorage.setItem(getScopedStorageKey('vehicle_year'), settings.vehicle_year);
    localStorage.setItem(getScopedStorageKey('transmission'), settings.transmission);
    localStorage.setItem(getScopedStorageKey('fuel_type'), settings.fuel_type);
    localStorage.setItem(getScopedStorageKey('vehicle_class'), settings.vehicle_class);
    localStorage.setItem(getScopedStorageKey('fuel_consumption'), settings.avg_consumption);
    localStorage.setItem(getScopedStorageKey('avoid_highways'), String(settings.avoid_highways));
    localStorage.setItem(getScopedStorageKey('avoid_tolls'), String(settings.avoid_tolls));

    setSaveMessage('Settings saved');
    setTimeout(() => setSaveMessage(''), 1800);
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    setVehicleSearch('');
  };

  const estimateConsumption = () => {
    const estimate = estimateGeneralFuelConsumption({
      make: settings.vehicle_make,
      model: `${settings.vehicle_model} ${settings.vehicle_variant}`.trim(),
      fuelType: settings.fuel_type
    });

    setSettings((prev) => ({
      ...prev,
      avg_consumption: String(estimate.value)
    }));
    setEstimateMessage(`${estimate.reason} Set to ${estimate.value} L/100km.`);
    setTimeout(() => setEstimateMessage(''), 2500);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.setItem(getScopedStorageKey('recent_searches'), JSON.stringify([]));
  };

  if (!user) {
    return (
      <div className="app-container">
        <Header />
        <main className="content profile-content">
          <div className="card profile-auth-card">
            <h2>Sign in required</h2>
            <p>Sign in from the homepage to view your profile and settings.</p>
            <button className="btn-primary profile-home-btn" onClick={() => navigate('/')}>
              <FaArrowLeft size={12} />
              Back to Home
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Seo
        title="Your Whe2 Profile"
        description="Manage vehicle preferences, account settings and recent activity in Whe2."
        path="/profile"
        robots="noindex,nofollow"
      />
      <Header />
      <main className="content profile-content">
        <div className="profile-top-row">
          <button className="btn-secondary profile-back-btn" onClick={() => navigate('/')}>
            <FaArrowLeft size={12} />
            Home
          </button>
          <button className="btn-secondary profile-signout-btn" onClick={logout}>
            <FaSignOutAlt size={12} />
            Sign out
          </button>
        </div>

        <div className="card profile-card">
          <div className="profile-user-row">
            {userPhotoURL && !profileImageFailed ? (
              <img
                src={userPhotoURL}
                alt={user.displayName || user.email}
                className="profile-avatar"
                onError={() => setProfileImageFailed(true)}
              />
            ) : (
              <FaUserCircle size={54} />
            )}
            <div className="profile-user-meta">
              <h2>{user.displayName || 'Signed-in User'}</h2>
              <p>{user.email}</p>
            </div>
          </div>
        </div>

        <div className="card profile-settings-card">
          <h3>Settings</h3>

          <div className="profile-settings-grid">
            <label className="profile-field">
              <span><FaCar size={12} /> Vehicle search</span>
              <div className="profile-input-wrap">
                <input
                  type="text"
                  placeholder="Search make, model, variant (e.g., Toyota Corolla 1.8 XR)"
                  value={vehicleSearch}
                  onChange={(e) => setVehicleSearch(e.target.value)}
                  onFocus={() => setShowVehicleSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowVehicleSuggestions(false), 120)}
                />
                {showVehicleSuggestions && filteredVehicleSuggestions.length > 0 && (
                  <div className="profile-autocomplete-list">
                    {filteredVehicleSuggestions.map((entry) => (
                      <button
                        key={entry.key}
                        type="button"
                        className="profile-autocomplete-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleVehicleSearchSelect(entry);
                          setShowVehicleSuggestions(false);
                        }}
                      >
                        <span>{entry.label}</span>
                        <small>{entry.fuel_type || settings.fuel_type} • {entry.vehicle_class || settings.vehicle_class}{entry.year ? ` • ${entry.year}` : ''}</small>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </label>

            <label className="profile-field">
              <span><FaRoad size={12} /> Model year</span>
              <select
                value={settings.vehicle_year}
                onChange={(e) => handleVehicleYearChange(e.target.value)}
              >
                <option value="">Select year</option>
                {yearSuggestions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </label>

            <label className="profile-field">
              <span><FaGasPump size={12} /> Fuel type</span>
              <select
                value={settings.fuel_type}
                onChange={(e) => handleSettingChange('fuel_type', e.target.value)}
                disabled={fuelTypeOptions.length === 1}
              >
                {fuelTypeOptions.map((fuelType) => (
                  <option key={fuelType} value={fuelType}>{fuelType}</option>
                ))}
              </select>
            </label>

            <label className="profile-field">
              <span><FaCar size={12} /> Transmission</span>
              <select
                value={settings.transmission}
                onChange={(e) => handleSettingChange('transmission', e.target.value)}
                disabled={transmissionOptions.length === 1}
              >
                {transmissionOptions.map((transmission) => (
                  <option key={transmission} value={transmission}>{transmission}</option>
                ))}
              </select>
            </label>

            <label className="profile-field">
              <span><FaCar size={12} /> Vehicle class</span>
              <select
                value={settings.vehicle_class}
                onChange={(e) => handleSettingChange('vehicle_class', e.target.value)}
              >
                <option value="Class 1">Class 1 - All Light Vehicles</option>
                <option value="Class 2">Class 2 - 2 Axles</option>
                <option value="Class 3">Class 3 - 3 or 4 Axles</option>
                <option value="Class 4">Class 4 - 5 or more Axles</option>
              </select>
            </label>

            <label className="profile-field">
              <span><FaRoad size={12} /> Avg consumption (L/100km)</span>
              <input
                type="number"
                min="1"
                max="30"
                step="0.1"
                value={settings.avg_consumption}
                onChange={(e) => handleSettingChange('avg_consumption', e.target.value)}
              />
            </label>

            <div className="profile-estimate-wrap">
              <button
                type="button"
                className="btn-secondary profile-estimate-btn"
                onClick={estimateConsumption}
              >
                <FaCalculator size={12} />
                Estimate Using AI
              </button>
              {estimateMessage && <span className="profile-estimate-message">{estimateMessage}</span>}
            </div>

            <label className="profile-toggle">
              <input
                type="checkbox"
                checked={settings.avoid_highways}
                onChange={(e) => handleSettingChange('avoid_highways', e.target.checked)}
              />
              Avoid highways by default
            </label>

            <label className="profile-toggle">
              <input
                type="checkbox"
                checked={settings.avoid_tolls}
                onChange={(e) => handleSettingChange('avoid_tolls', e.target.checked)}
              />
              Avoid tolls by default
            </label>
          </div>

          <div className="profile-actions">
            <button className="btn-primary profile-save-btn" onClick={persistSettings}>
              <FaSave size={12} />
              Save settings
            </button>
            <button className="btn-secondary profile-reset-btn" onClick={resetSettings}>
              Reset
            </button>
            {saveMessage && <span className="profile-save-message">{saveMessage}</span>}
          </div>
        </div>

        <div className="card profile-data-card">
          <div className="profile-data-header">
            <h3><FaHistory size={13} /> Recent Searches</h3>
            <button className="profile-link-action" onClick={clearRecentSearches}>Clear all</button>
          </div>
          {recentSearches.length === 0 ? (
            <p className="profile-empty">No recent searches yet.</p>
          ) : (
            <div className="profile-list">
              {recentSearches.map((search) => (
                <div className="profile-list-item" key={search.id}>
                  <div>
                    <strong>{search.query}</strong>
                    <span>{new Date(search.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
